import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Drawer, Spin, message } from "antd";
import { CloseOutlined, UserOutlined } from "@ant-design/icons";
import API_MANAGER from "../../../API";
import { joinRoom, socket } from "../../Socket/socket";
import ChatScreen from "../../../pages/Chat/ChatScreen";

const getProfileImageUrl = (profileImage) => {
  if (!profileImage) return null;
  if (String(profileImage).startsWith("http")) return profileImage;
  return `https://dev-documents-cinchy.s3.ap-southeast-2.amazonaws.com/${profileImage}`;
};

function ApplicantChatDrawer({
  open,
  onClose,
  memberId,
  candidateName,
  candidateImage,
}) {
  const currentUserId =
    localStorage.getItem("userId") || localStorage.getItem("userID");
  const [loading, setLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);

  const avatarUrl = useMemo(
    () => getProfileImageUrl(candidateImage),
    [candidateImage]
  );

  const startDirectChat = useCallback(async () => {
    if (!memberId || !currentUserId) {
      message.error("Unable to start chat.");
      return;
    }
    setLoading(true);
    setSelectedChat(null);
    try {
      const response = await API_MANAGER.createChat({
        members: [currentUserId, memberId],
        type: "Direct",
      });
      if (response?.data?.isSuccess === false) {
        throw new Error(
          response?.data?.message?.message ||
            response?.data?.message ||
            "Failed to start chat."
        );
      }
      const room = response?.data?.data || response?.data;
      const createdRoomId = room?._id || room?.id || room?.roomId;
      if (!createdRoomId) {
        throw new Error("Chat room not created.");
      }

      if (socket) {
        joinRoom(createdRoomId);
      }

      setSelectedChat({
        id: createdRoomId,
        roomId: createdRoomId,
        name: candidateName || "Candidate",
        avatarUrl,
        otherMemberId: memberId,
        type: room?.type || "Direct",
        isGroup: false,
        members: Array.isArray(room?.members) ? room.members : [],
        statusLabel: "DIRECT",
      });
    } catch (error) {
      message.error(
        error?.response?.data?.message?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to start chat."
      );
    } finally {
      setLoading(false);
    }
  }, [memberId, currentUserId, candidateName, avatarUrl]);

  useEffect(() => {
    if (open && memberId) {
      startDirectChat();
    }
    if (!open) {
      setSelectedChat(null);
    }
  }, [open, memberId, startDirectChat]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      closable={false}
      destroyOnClose
      getContainer={document.body}
      zIndex={10000}
      className="applicant-chat-drawer"
      bodyStyle={{ padding: 0, height: "100%" }}
      headerStyle={{ display: "none" }}
    >
      <div className="applicant-chat-shell applicant-chat-shell--fullscreen">
        <div className="applicant-chat-header">
          <div className="applicant-chat-person">
            <Avatar
              size={40}
              src={avatarUrl}
              icon={!avatarUrl ? <UserOutlined /> : undefined}
              className="applicant-chat-avatar"
              style={{ backgroundColor: "#E5E7EB", color: "#091647" }}
            >
              {!avatarUrl && (candidateName || "C").charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <p className="applicant-chat-name">
                {candidateName || "Candidate"}
              </p>
              <span className="applicant-chat-badge">Direct chat</span>
            </div>
          </div>
          <Button
            type="text"
            className="applicant-chat-close"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </div>

        <div className="applicant-chat-screen">
          {loading || !selectedChat ? (
            <div className="applicant-chat-empty">
              <Spin />
              <p>Opening conversation...</p>
            </div>
          ) : (
            <ChatScreen
              selectedChat={selectedChat}
              getProfileImageUrl={getProfileImageUrl}
              embedded
            />
          )}
        </div>
      </div>
    </Drawer>
  );
}

export default ApplicantChatDrawer;
